<template>
  <div v-for="item in formattedItems" :key="item.id" class="pt-24 first:pt-0">
    <div class="flex" data-qa="product">
      <div class="relative">
        <div
          class="border-static-default-low rounded-sm flex h-40 w-40 items-center justify-center border"
        >
          <RevIllustration
            alt=""
            class="h-32 w-32"
            :height="32"
            :src="item.image"
            :width="32"
          />
        </div>
        <RevBadge
          v-if="item.quantity"
          class="absolute right-0 top-0 -translate-y-1/2 translate-x-1/2"
          :count="item.quantity"
        />
      </div>

      <div class="ml-24 w-full">
        <div class="space-y-8">
          <div class="flex justify-between gap-24">
            <h2 class="body-2-bold" data-qa="product-name">
              {{ item.title }}
            </h2>
            <div class="body-2" data-qa="product-price">
              {{ item.price }}
            </div>
          </div>

          <div
            v-if="
              item.mobilePlan?.selectedOffer &&
              hasSubsidy(item.mobilePlan.selectedOffer)
            "
            class="text-static-success-hi caption"
            data-qa="mobile-plan-subsidy"
          >
            {{
              i18n(translations.mobilePlanSubsidy, {
                partnerName: item.mobilePlan.selectedOffer.partner.label,
                subsidy: i18n.price(item.mobilePlan.selectedOffer.subsidy),
              })
            }}
          </div>

          <div class="body-2 flex justify-between" data-qa="shipping">
            <div>{{ i18n(translations.shipping) }}</div>
            <div>{{ item.shippingPrice }}</div>
          </div>
        </div>

        <BouyguesMobilePlan
          v-if="
            item.mobilePlan &&
            isBouyguesOffer(item.mobilePlan.selectedOffer) &&
            item.mobilePlan.selectedOffer.type !== 'IN_STORE'
          "
          class="mt-24"
          data-qa="mobile-plan"
          data-test="bouygues-mobile-plan"
          :offer="item.mobilePlan.selectedOffer"
        />

        <div
          v-if="item.insurance"
          class="body-2 mt-8 flex justify-between"
          data-qa="insurance"
        >
          <div>{{ item.insurance.title }}</div>
          <div>{{ i18n.price(item.insurance.price) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { DEFAULT_INSURANCE_ID } from '@backmarket/nuxt-layer-checkout/constants'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevBadge } from '@ds/components/Badge'
import { RevIllustration } from '@ds/components/Illustration'

import { isBouyguesOffer } from '~/scopes/checkout/utils/isBouyguesOffer'
import { hasSubsidy } from '~/scopes/checkout/utils/mobile-plan'

import BouyguesMobilePlan from '../BouyguesMobilePlan/BouyguesMobilePlan.vue'

import translations from './Items.translations'

const props = defineProps<{
  items: CartItem[]
}>()

const i18n = useI18n()

const formattedItems = computed(() =>
  props.items.map((item) => {
    return {
      id: item.listingId,
      image: item.image,
      price: i18n.price(item.price),
      shippingPrice:
        item.shippingSelected.sumShippingFeePrice === '0'
          ? i18n(translations.free)
          : i18n.price(item.shippingSelected.sumShippingFeePrice),
      title: item.title,
      insurance: item.insuranceOffers?.find(
        (offer) =>
          offer.selected && offer.id.toString() !== DEFAULT_INSURANCE_ID,
      ),
      quantity: item.quantity,
      mobilePlan: item.mobilePlan,
    }
  }),
)
</script>
